import React, { useState, useRef } from 'react';
import {
	Box,
	Button,
	FormControl,
	InputLabel,
	Select,
	Typography,
	MenuItem,
	Paper,
} from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { createShiftRequest, updateShiftRequest } from 'core/API/shift-requests';
import { ShiftRequest, CreateShiftRequestPayload, ShiftRequestType } from 'types/shift-requests';
import { getCachedShiftRequestTypes } from 'core/utils/shiftRequestTypesCache';
import AvailabilityForm from './forms/AvailabilityForm';
import TimeOffForm from './forms/TimeOffForm';
import CoverRequestForm from './forms/CoverRequestForm';
import PickupShiftForm from './forms/PickupShiftForm';
import TradeShiftForm from './forms/TradeShiftForm';
import './EmployeeRequestModal.style.scss';

interface EmployeeRequestModalProps {
	closeModal: () => void;
	refetchSchedules?: () => void;
	currentWeekStart?: Date;
	currentWeekEnd?: Date;
	editRequest?: ShiftRequest | null;
	defaultRequestType?: RequestType;
	hideRequestTypeSelector?: boolean;
}

type RequestType = 'availability' | 'time-off' | 'cover' | 'pickup' | 'trade' | '';

const EmployeeRequestModal: React.FC<EmployeeRequestModalProps> = ({
	closeModal,
	refetchSchedules,
	currentWeekStart,
	currentWeekEnd,
	editRequest,
	defaultRequestType = '',
	hideRequestTypeSelector = false,
}) => {
	const { workplaceId } = useWorkplacesContext();
	const { showToast } = useToastContext();
	const queryClient = useQueryClient();
	const [requestType, setRequestType] = useState<RequestType>(defaultRequestType);
	const [selectedTypeData, setSelectedTypeData] = useState<ShiftRequestType | null>(null);
	const formRef = useRef<any>(null);

	// Reset form when week changes (unless editing an existing request)
	React.useEffect(() => {
		if (!editRequest && formRef.current) {
			formRef.current.resetForm();
		}
	}, [currentWeekStart, currentWeekEnd, editRequest]);

	// Get shift request types from cache (populated by SchedulePage)
	const typesData = React.useMemo(() => {
		const cached = getCachedShiftRequestTypes();
		if (!cached) return null;
		return {
			'member': cached,
			'@context': '',
			'@id': '',
			'@type': '',
			'totalItems': cached.length,
		};
	}, []);

	// Set up edit mode if editRequest is provided
	React.useEffect(() => {
		if (editRequest && typesData?.['member']) {
			const type = typesData['member'].find((t: ShiftRequestType) => 
				editRequest.type.id === t.id
			);
			if (type) {
				setSelectedTypeData(type);
				setRequestType(getFormTypeFromLabel(type.label));
			}
		}
	}, [editRequest, typesData]);

	// Set up selectedTypeData if defaultRequestType is provided
	React.useEffect(() => {
		if (defaultRequestType && typesData?.['member'] && !editRequest) {
			const type = typesData['member'].find((t: ShiftRequestType) => 
				getFormTypeFromLabel(t.label) === defaultRequestType
			);
			if (type) {
				setSelectedTypeData(type);
			}
		}
	}, [defaultRequestType, typesData, editRequest]);

	// Create mutation
	const { mutate: createMutation, isLoading: isCreating } = useMutation(
		createShiftRequest,
		{
			onSuccess: () => {
				showToast('success', 'Shift request created successfully!');
				queryClient.invalidateQueries(['shift-requests']);
				if (refetchSchedules) {
					refetchSchedules();
				}
				closeModal();
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to create shift request');
			},
		}
	);

	// Update mutation
	const { mutate: updateMutation, isLoading: isUpdating } = useMutation(
		({ id, payload }: { id: number; payload: Partial<CreateShiftRequestPayload> }) =>
			updateShiftRequest(id, payload),
		{
			onSuccess: () => {
				showToast('success', 'Shift request updated successfully!');
				queryClient.invalidateQueries(['shift-requests']);
				if (refetchSchedules) {
					refetchSchedules();
				}
				closeModal();
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to update shift request');
			},
		}
	);

	const handleSubmitClick = () => {
		if (formRef.current) {
			formRef.current.submitForm();
		}
	};

	const getFormTypeFromLabel = (label: string): RequestType => {
		const labelLower = label.toLowerCase();
		if (labelLower.includes('availability')) return 'availability';
		if (labelLower.includes('time off')) return 'time-off';
		if (labelLower.includes('cover')) return 'cover';
		if (labelLower.includes('pickup') || labelLower.includes('pick up')) return 'pickup';
		if (labelLower.includes('trade')) return 'trade';
		return '';
	};

	const handleSubmit = async (formValues: any) => {
		try {
			if (!selectedTypeData) {
				showToast('error', 'Please select a request type');
				return;
			}

			const formType = getFormTypeFromLabel(selectedTypeData.label);

			// Handle Availability requests (multiple requests, one per day)
			if (formType === 'availability') {
				const currentUserId = localStorage.getItem('userId');
				const requests = formValues.availabilityRequests || [];
				
				const promises = requests.map((req: any) => {
					const payload: CreateShiftRequestPayload = {
						requestType: 'availability',
						workplace: `api/workplaces/${workplaceId}`,
						startAt: req.startTime || null,
						endAt: req.endTime || null,
						employeeRequest: currentUserId ? `api/users/${currentUserId}` : null,
						note: formValues.notes || null, // Mandatory for availability
						day: req.day ? `api/days/${req.day}` : null, // Mandatory for availability
					};
					return createShiftRequest(payload);
				});

				await Promise.all(promises);
				showToast('success', 'Availability requests created successfully!');
				queryClient.invalidateQueries(['shift-requests']);
				if (refetchSchedules) {
					refetchSchedules();
				}
				closeModal();
				return;
			}

			// Handle Time Off requests
			if (formType === 'time-off') {
				const currentUserId = localStorage.getItem('userId');
				const payload: CreateShiftRequestPayload = {
					requestType: 'time-off',
					workplace: `api/workplaces/${workplaceId}`,
					startAt: formValues.startDateTime || null,
					endAt: formValues.endDateTime || null,
					employeeRequest: currentUserId ? `api/users/${currentUserId}` : null,
					allDay: false,
					timeOffReasons: formValues.timeOffReasons || null, // This is the timeoff reason label/IRI
					reasonDescription: formValues.reasonDescription || null, // Description from selected time off reason
					reasonNote: formValues.reasonNote || null, // Note from selected time off reason
					note: formValues.notes || null, // Employee's additional notes
				};

				if (editRequest) {
					updateMutation({ id: editRequest.id, payload });
				} else {
					createMutation(payload);
				}
				return;
			}

			// Handle Cover Request
			if (formType === 'cover') {
				const currentUserId = localStorage.getItem('userId');
				const payload: CreateShiftRequestPayload = {
					requestType: 'cover',
					workplace: `api/workplaces/${workplaceId}`,
					shiftRequest: formValues.shiftId ? `api/shifts/${formValues.shiftId}` : null, // Shift to be covered
					employeeRequest: formValues.employeeId ? `api/users/${formValues.employeeId}` : null, // Employee who will cover the shift
					reason: formValues.reason || null, // Text reason
				};

				if (editRequest) {
					updateMutation({ id: editRequest.id, payload });
				} else {
					createMutation(payload);
				}
				return;
			}

			// Handle Pickup Shift requests
			if (formType === 'pickup') {
				const currentUserId = localStorage.getItem('userId');
				const payload: CreateShiftRequestPayload = {
					requestType: 'pickup',
					workplace: `api/workplaces/${workplaceId}`,
					shiftRequest: formValues.shiftId ? `api/shifts/${formValues.shiftId}` : null, // Shift to be picked up
					employeeRequest: currentUserId ? `api/users/${currentUserId}` : null, // Employee submitting the request
					note: formValues.notes || null,
				};

				if (editRequest) {
					updateMutation({ id: editRequest.id, payload });
				} else {
					createMutation(payload);
				}
				return;
			}

			// Handle Trade Shift requests
			if (formType === 'trade') {
				const currentUserId = localStorage.getItem('userId');
				const payload: CreateShiftRequestPayload = {
					requestType: 'trade',
					workplace: `api/workplaces/${workplaceId}`,
					startAt: null,
					endAt: null,
					shiftRequest: formValues.myShiftId ? `api/shifts/${formValues.myShiftId}` : null, // Current user's shift
					employeeRequest: currentUserId ? `api/users/${currentUserId}` : null, // Current user
					employeeAssigned: formValues.tradeWithEmployeeId 
						? `api/users/${formValues.tradeWithEmployeeId}` 
						: null, // User with the second shift
					shiftAssigned: formValues.theirShiftId ? `api/shifts/${formValues.theirShiftId}` : null, // Second shift to be traded
					note: formValues.notes || null,
				};

				if (editRequest) {
					updateMutation({ id: editRequest.id, payload });
				} else {
					createMutation(payload);
				}
				return;
			}

			showToast('error', 'Unknown request type');
		} catch (error: any) {
			showToast('error', error?.message || 'Failed to submit request');
		}
	};

	const handleTypeChange = (typeId: string) => {
		const type = typesData?.['member']?.find((t: ShiftRequestType) => t.id.toString() === typeId);
		if (type) {
			setSelectedTypeData(type);
			setRequestType(getFormTypeFromLabel(type.label));
		}
	};

	const renderForm = () => {
		switch (requestType) {
			case 'availability':
				return <AvailabilityForm onSubmit={handleSubmit} onCancel={closeModal} formRef={formRef} />;
			case 'time-off':
				return <TimeOffForm onSubmit={handleSubmit} onCancel={closeModal} formRef={formRef} />;
			case 'cover':
				return (
					<CoverRequestForm
						onSubmit={handleSubmit}
						onCancel={closeModal}
						currentWeekStart={currentWeekStart}
						currentWeekEnd={currentWeekEnd}
						formRef={formRef}
					/>
				);
			case 'pickup':
				return (
					<PickupShiftForm
						onSubmit={handleSubmit}
						onCancel={closeModal}
						currentWeekStart={currentWeekStart}
						currentWeekEnd={currentWeekEnd}
						formRef={formRef}
					/>
				);
			case 'trade':
				return (
					<TradeShiftForm
						onSubmit={handleSubmit}
						onCancel={closeModal}
						currentWeekStart={currentWeekStart}
						currentWeekEnd={currentWeekEnd}
						formRef={formRef}
					/>
				);
			default:
				return null;
		}
	};

	const types = typesData?.['member'] || [];
	const isSaving = isCreating || isUpdating;

	return (
		<Paper sx={{ minHeight: '100vh', py: 3, borderRadius: 3, boxShadow: hideRequestTypeSelector ? 0 : 2 }}>
			<Box maxWidth="1100px" mx="auto" px={3}>
				{/* Header */}
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
					<Button
						onClick={closeModal}
						sx={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							textTransform: 'none',
							fontWeight: 500,
							px: 3,
							py: 0.75,
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
					>
						Back
					</Button>
					<Typography variant="h5" sx={{ fontWeight: 600 }}>
						{editRequest ? 'Edit Request' : hideRequestTypeSelector ? 'Availability Request' : 'New Request'}
					</Typography>
					{requestType && (
						<Button
							onClick={handleSubmitClick}
							disabled={isSaving}
							sx={{
								backgroundColor: 'rgb(68, 99, 136)',
								color: '#fff',
								textTransform: 'none',
								fontWeight: 500,
								px: 3,
								py: 0.75,
								'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
							}}
						>
							{isSaving ? 'Submitting...' : 'Submit'}
						</Button>
					)}
					{!requestType && <Box sx={{ width: '120px' }} />}
				</Box>

				{/* Request Form */}
				<Box sx={{ p: 4 }}>
					{!hideRequestTypeSelector && (
						<FormControl fullWidth sx={{ mb: 3 }}>
							<InputLabel>Request Type</InputLabel>
							<Select
								value={selectedTypeData?.id.toString() || ''}
								onChange={(e) => handleTypeChange(e.target.value)}
								label="Request Type"
								disabled={!!editRequest}
							>
								<MenuItem value="">Select a request type</MenuItem>
								{types
									.filter((type: ShiftRequestType) => !type.label.toLowerCase().includes('trade'))
									.map((type: ShiftRequestType) => (
										<MenuItem key={type.id} value={type.id.toString()}>
											{type.label}
										</MenuItem>
									))
								}
							</Select>
						</FormControl>
					)}

					{renderForm()}
				</Box>
			</Box>
		</Paper>
	);
};

export default EmployeeRequestModal;
